import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopbar from '../../components/AdminTopbar';
import { PortfolioAPI } from '../../lib/api';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useNotification } from '../../contexts/NotificationContext';
import CustomSelect from '../../components/CustomSelect';
import LoadingState from '../components/LoadingState';

export default function PortfolioList(){
  const confirm = useConfirm();
  const notify = useNotification();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Filter states
  const [featuredFilter, setFeaturedFilter] = useState('all'); // 'all', 'featured', 'not-featured'
  




  useEffect(()=>{
    let mounted = true;
    (async()=>{
      setLoading(true); setError('');
      try{
        const { items } = await PortfolioAPI.list();
        if(mounted) setItems(items||[]);
      }catch(err){ if(mounted) setError(err.message||'Failed to load portfolio'); }
      finally{ if(mounted) setLoading(false); }
    })();
    return ()=>{ mounted=false };
  },[]);

  // Filter items
  const filtered = useMemo(() => {
    let result = items;
    
    // Filter by featured status
    if (featuredFilter === 'featured') {
      result = result.filter(i => i.featured === true);
    } else if (featuredFilter === 'not-featured') {
      result = result.filter(i => !i.featured);
    }
    
    // Filter by search term
    const term = q.trim().toLowerCase();
    if (term) {
      result = result.filter(i => 
        (i.title || '').toLowerCase().includes(term) || 
        (i.client || '').toLowerCase().includes(term) ||
        (i.description || '').toLowerCase().includes(term)
      );
    }
    
    return result;
  }, [q, items, featuredFilter]);

  const total = items.length;
  const featured = items.filter(i=>!!i.featured).length;

  // Toggle single selection
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(i => i._id));
    }
  };

  // Delete selected items
  async function handleDeleteSelected() {
    if (selectedIds.length === 0) {
      notify.warning('Please select projects to delete');
      return;
    }
    
    await confirm.show({
      title: 'Delete Projects',
      message: `Delete ${selectedIds.length} selected project(s)?`,
      variant: 'danger',
      confirmText: 'Delete',
      action: async () => {
        await Promise.all(selectedIds.map(id => PortfolioAPI.remove(id)));
        setItems(prev => prev.filter(i => !selectedIds.includes(i._id)));
        setSelectedIds([]);
        notify.success('Selected projects deleted successfully');
      }
    });
  }

  async function handleDelete(id){
    await confirm.show({
      title: 'Delete Project',
      message: 'Delete this item?',
      variant: 'danger',
      confirmText: 'Delete',
      action: async () => {
        await PortfolioAPI.remove(id); 
        setItems(prev=>prev.filter(i=>i._id!==id)); 
        notify.success('Project deleted');
      }
    });
  }

  const featuredOptions = [
    { value: 'all', label: 'All Projects' },
    { value: 'featured', label: 'Featured Only' },
    { value: 'not-featured', label: 'Not Featured' }
  ];

  const selectedFeaturedOption = featuredOptions.find(opt => opt.value === featuredFilter);

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <AdminTopbar />
        <div className="toolbar" style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'.6rem',flexWrap:'wrap'}}>
          <h1>Portfolio</h1>
          <div style={{display:'flex',gap:'.6rem',alignItems:'center',flexWrap:'wrap'}}>
            {/* Search Bar */}
            <div className="admin-search" style={{maxWidth:280}}>
              <span className="admin-search__icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8"/>
                  <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </span>
              <input className="admin-search__input" placeholder="Search projects..." value={q} onChange={e=>setQ(e.target.value)} />
            </div>

            <div style={{ width: '160px' }}>
              <CustomSelect
                options={featuredOptions}
                value={featuredFilter}
                onChange={setFeaturedFilter}
              />
            </div>

            <Link to="/admin/portfolio-categories" className="btn-secondary">Categories</Link>
            <Link to="/admin/tech-stack" className="btn-secondary">Tech Stack</Link>
            <Link to="/admin/reviews" className="btn-secondary">Reviews</Link>
            <Link to="/admin/portfolio/new" className="btn">Add Project</Link>
          </div>
        </div>

        {/* Totals strip */}
        {!loading && !error && (
          <div className="card" style={{marginTop:'.75rem', padding:'.5rem 1rem', display:'flex', gap:'.6rem', alignItems:'center', flexWrap:'wrap'}}>
            <span className="badge">Total: {total}</span>
            <span className="badge">Featured: {featured}</span>
            <span className="badge">Showing: {filtered.length}</span>
            {selectedIds.length > 0 && (
              <span className="badge" style={{background:'#3b82f6'}}>Selected: {selectedIds.length}</span>
            )}
          </div>
        )}

        {/* Bulk Actions Bar */}
        {!loading && !error && filtered.length > 0 && (
          <div className="card" style={{
            marginTop:'.75rem', 
            padding:'.5rem 1rem', 
            display:'flex', 
            gap:'.6rem', 
            alignItems:'center',
            justifyContent:'space-between',
            flexWrap:'wrap'
          }}>
            <div style={{display:'flex',gap:'.6rem',alignItems:'center'}}>
              <input 
                type="checkbox" 
                checked={selectedIds.length === filtered.length && filtered.length > 0}
                onChange={toggleSelectAll}
                style={{cursor:'pointer',width:'18px',height:'18px'}}
              />
              <span style={{fontSize:'.9rem'}}>
                {selectedIds.length === filtered.length && filtered.length > 0 
                  ? 'Deselect All' 
                  : 'Select All'}
              </span>
            </div>
            
            {selectedIds.length > 0 && (
              <button 
                className="btn-secondary" 
                onClick={handleDeleteSelected}
                style={{
                  borderColor:'#ef4444',
                  color:'#ef4444',
                  fontWeight:'500'
                }}
              >
                Delete Selected ({selectedIds.length})
              </button>
            )}
          </div>
        )}

        {loading && <LoadingState message="Loading portfolio..." />}
        {error && <div className="card" style={{marginTop:'1rem', color:'#ef4444'}}>{error}</div>}

        {!loading && !error && (
          filtered.length ? (
            <div className="grid three" style={{marginTop:'1rem'}}>
              {filtered.map(p => (
                <div 
                  className="card" 
                  key={p._id} 
                  style={{
                    display:'grid',
                    gap:'.5rem',
                    border: selectedIds.includes(p._id) ? '2px solid #3b82f6' : undefined,
                    background: selectedIds.includes(p._id) ? 'rgba(59, 130, 246, 0.1)' : undefined
                  }}
                >
                  {/* Checkbox and Image */}
                  <div style={{position:'relative'}}>
                    {p.thumbnails?.[0] && (
                      <img src={p.thumbnails[0]} alt={p.title} style={{width:'100%',borderRadius:'12px'}} />
                    )}
                    <input 
                      type="checkbox" 
                      checked={selectedIds.includes(p._id)}
                      onChange={() => toggleSelect(p._id)}
                      style={{
                        cursor:'pointer',
                        width:'20px',
                        height:'20px',
                        position: p.thumbnails?.[0] ? 'absolute' : 'static',
                        top: p.thumbnails?.[0] ? '0.5rem' : 'auto',
                        left: p.thumbnails?.[0] ? '0.5rem' : 'auto',
                        margin: p.thumbnails?.[0] ? '0' : '0 0 0.5rem 0'
                      }}
                    />
                    {p.featured && (
                      <span 
                        className="badge" 
                        style={{
                          position: p.thumbnails?.[0] ? 'absolute' : 'static',
                          top: p.thumbnails?.[0] ? '0.5rem' : 'auto',
                          right: p.thumbnails?.[0] ? '0.5rem' : 'auto',
                          background: '#f59e0b',
                          marginLeft: p.thumbnails?.[0] ? '0' : 'auto'
                        }}
                      >
                        ⭐ Featured
                      </span>
                    )}
                  </div>
                  
                  <h3 style={{margin:0}}>{p.title}</h3>
                  {p.client && <small className="muted">Client: {p.client}</small>}
                  
                  <div style={{display:'flex',gap:'.4rem',marginTop:'.25rem'}}>
                    <button className="btn-secondary" onClick={()=>navigate(`/admin/portfolio/${p._id}`)}>Edit</button>
                    <button className="btn-secondary" onClick={()=>handleDelete(p._id)} style={{borderColor:'#ef4444',color:'#ef4444'}}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card" style={{marginTop:'1rem',textAlign:'center',padding:'2rem'}}>
              <h3 style={{marginTop:0}}>
                {featuredFilter !== 'all' || q.trim()
                  ? 'No Projects Found'
                  : 'No Projects Yet'
                }
              </h3>
              <p style={{opacity:.8,marginTop:'.25rem'}}>
                {featuredFilter !== 'all' || q.trim()
                  ? 'Try changing the filter or search term.'
                  : 'Add your first portfolio item.'
                }
              </p>
              <Link to="/admin/portfolio/new" className="btn" style={{marginTop:'.5rem'}}>Add a Project</Link>
            </div>
          )
        )}
      </main>
    </div>
  );
}